import { clsx } from 'clsx'
import { createContext, ReactNode, useContext, useId, useState } from 'react'
import { CaretDownIcon } from './icons/CaretDownIcon'

const AccordionDepth = createContext(0)

type Props = {
  children: ReactNode
  className?: string
}
export function Accordion({ className, children }: Props) {
  return <div className={clsx('flex min-w-0 flex-col gap-2', className)}>{children}</div>
}

export function AccordionItem({
  title,
  iconTip,
  statusLabel,
  defaultExpanded = false,
  children
}: {
  title: string
  iconTip?: ReactNode
  statusLabel?: string
  defaultExpanded?: boolean
  children: ReactNode
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const depth = useContext(AccordionDepth)
  const contentId = useId()

  // Nested items share their parent's surface; each content level owns its indentation.
  return (
    <div className="border-t border-gray-200">
      <div className="flex items-center gap-2 text-gray-900">
        <button
          type="button"
          className={clsx(
            'flex min-w-0 flex-1 items-center gap-3 text-left font-semibold focus-visible:outline-blue-600',
            depth == 0 && 'py-7 text-[18px]',
            depth > 0 && 'py-4 text-lg'
          )}
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setExpanded(!expanded)}
        >
          <CaretDownIcon
            className={clsx('shrink-0 text-gray-500', {
              'size-5': depth == 0,
              'size-4': depth > 0,
              '-rotate-90': !expanded
            })}
          />
          <span>{title}</span>
          {statusLabel && (
            <span className="ml-auto shrink-0 text-sm font-normal text-gray-600">
              {statusLabel}
            </span>
          )}
        </button>
        {iconTip}
      </div>
      <div id={contentId} hidden={!expanded} className="pb-4 pl-7 text-base text-gray-600 sm:pl-8">
        {expanded && (
          <AccordionDepth.Provider value={depth + 1}>{children}</AccordionDepth.Provider>
        )}
      </div>
    </div>
  )
}
